/**
 * Binance Connection Modal
 *
 * User-friendly modal for connecting a Binance account
 * Provides step-by-step instructions and validation
 */

import React, { useState } from 'react';
import { exchangeAPI } from '../services/tradingBotAPI';
import { useToast } from './Toast';

interface BinanceConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnectionSuccess: () => void;
}

export const BinanceConnectionModal: React.FC<BinanceConnectionModalProps> = ({
  isOpen,
  onClose,
  onConnectionSuccess,
}) => {
  const toast = useToast();
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [testnet, setTestnet] = useState(true);
  const [label, setLabel] = useState('My Binance Account');

  const [step, setStep] = useState<'form' | 'testing' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState('');
  const [accountInfo, setAccountInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const resetForm = () => {
    setApiKey('');
    setApiSecret('');
    setTestnet(true);
    setLabel('My Binance Account');
    setStep('form');
    setErrorMessage('');
    setAccountInfo(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleTestConnection = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) {
      setErrorMessage('Veuillez remplir les deux champs (API Key et Secret)');
      return;
    }

    setLoading(true);
    setStep('testing');
    setErrorMessage('');

    try {
      const response = await exchangeAPI.test(apiKey, apiSecret, testnet);

      if (response.success && response.data?.connected) {
        setAccountInfo(response.data.accountInfo);
        setStep('success');
      } else {
        setErrorMessage(response.error?.message || 'Test de connexion échoué');
        setStep('error');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error?.message || error.message || 'Erreur lors du test de connexion';
      setErrorMessage(errorMsg);
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConnection = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await exchangeAPI.connect(apiKey, apiSecret, testnet, label);

      if (response.success) {
        toast.success('Connexion Binance enregistrée avec succès !');
        onConnectionSuccess();
        handleClose();
      } else {
        setErrorMessage(response.error?.message || 'Échec de l\'enregistrement');
        setStep('error');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error?.message || error.message || 'Erreur lors de l\'enregistrement';
      setErrorMessage(errorMsg);
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#1a1a2e',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        color: '#e0e0e0',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
            🔗 Connecter votre compte Binance
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#e0e0e0',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ×
          </button>
        </div>

        {/* Instructions */}
        <div style={{
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#60a5fa' }}>
            📋 Instructions
          </h3>
          <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
            <li>
              <strong>Créez un compte Binance Testnet</strong> (gratuit, sans argent réel) :
              <a
                href="https://testnet.binance.vision/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#60a5fa', marginLeft: '8px' }}
              >
                testnet.binance.vision
              </a>
            </li>
            <li>
              <strong>Générez une API Key</strong> :
              <ul style={{ marginTop: '8px' }}>
                <li>Connectez-vous au testnet</li>
                <li>Allez dans "Generate HMAC_SHA256 Key"</li>
                <li>Copiez l'API Key et l'API Secret</li>
              </ul>
            </li>
            <li>
              <strong>Collez vos clés ci-dessous</strong> et testez la connexion
            </li>
          </ol>
        </div>

        {/* Form */}
        <div style={{ marginBottom: '24px' }}>
          {/* Label */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Nom de la connexion
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Mon compte Binance Testnet"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#0f0f1e',
                border: '1px solid #2d3748',
                borderRadius: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
              }}
            />
          </div>

          {/* API Key */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              API Key <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Collez votre Binance API Key"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#0f0f1e',
                border: '1px solid #2d3748',
                borderRadius: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontFamily: 'monospace',
              }}
            />
          </div>

          {/* API Secret */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              API Secret <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder="Collez votre Binance API Secret"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#0f0f1e',
                border: '1px solid #2d3748',
                borderRadius: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontFamily: 'monospace',
              }}
            />
          </div>

          {/* Testnet Toggle */}
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="testnet"
              checked={testnet}
              onChange={(e) => setTestnet(e.target.checked)}
              style={{ marginRight: '8px', width: '18px', height: '18px' }}
            />
            <label htmlFor="testnet" style={{ cursor: 'pointer' }}>
              Utiliser le testnet Binance (recommandé pour débuter)
            </label>
          </div>
        </div>

        {/* Connection Test Success */}
        {step === 'success' && accountInfo && (
          <div style={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#10b981' }}>
              ✅ Connexion testée avec succès !
            </h4>
            <div style={{ fontSize: '14px' }}>
              <p style={{ margin: '4px 0' }}>
                <strong>Trading autorisé :</strong> {accountInfo.canTrade ? '✅ Oui' : '❌ Non'}
              </p>
              <p style={{ margin: '4px 0' }}>
                <strong>Dépôts autorisés :</strong> {accountInfo.canDeposit ? '✅ Oui' : '❌ Non'}
              </p>
              <p style={{ margin: '4px 0' }}>
                <strong>Retraits autorisés :</strong> {accountInfo.canWithdraw ? '✅ Oui' : '❌ Non'}
              </p>
              {accountInfo.balances && accountInfo.balances.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <strong>Soldes disponibles :</strong>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {accountInfo.balances.slice(0, 5).map((bal: any, idx: number) => (
                      <li key={idx}>
                        {bal.asset}: {bal.total.toFixed(8)} ({bal.free.toFixed(8)} libre)
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {step === 'error' && errorMessage && (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#f87171',
          }}>
            <strong>❌ Erreur :</strong> {errorMessage}
          </div>
        )}

        {/* Testing */}
        {step === 'testing' && (
          <div style={{
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
            <p style={{ margin: 0 }}>Test de la connexion en cours...</p>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#374151',
              color: '#e0e0e0',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: loading ? 0.5 : 1,
            }}
          >
            Annuler
          </button>

          {step !== 'success' && (
            <button
              onClick={handleTestConnection}
              disabled={loading || !apiKey.trim() || !apiSecret.trim()}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: (loading || !apiKey.trim() || !apiSecret.trim()) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: (loading || !apiKey.trim() || !apiSecret.trim()) ? 0.5 : 1,
              }}
            >
              {loading ? '⏳ Test en cours...' : '🧪 Tester la connexion'}
            </button>
          )}

          {step === 'success' && (
            <button
              onClick={handleSaveConnection}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: loading ? 0.5 : 1,
              }}
            >
              {loading ? '⏳ Enregistrement...' : '✅ Enregistrer et Connecter'}
            </button>
          )}
        </div>

        {/* Security Note */}
        <div style={{
          marginTop: '24px',
          fontSize: '12px',
          color: '#9ca3af',
          textAlign: 'center',
          borderTop: '1px solid #2d3748',
          paddingTop: '16px',
        }}>
          🔒 Vos clés API sont chiffrées avec AES-256-GCM avant d'être stockées dans notre base de données.
        </div>
      </div>
    </div>
  );
};
